<template>
  <div v-bind="$attrs" class="so-alert">
    <v-alert
      ref="alert"
      :title="title"
      rounded
      variant="outlined"
      data-testid="so-alert"
      :class="`so alert so-alert--${type}`"
      v-bind="alertAttrs"
    >
      <template v-if="showCloseButton" #close>
        <v-btn
          icon="close"
          variant="plain"
          density="compact"
          class="d-block"
          data-testid="so-alert-close-btn"
          @click="$emit('close')"
        />
      </template>
      <div data-testid="alert-slot-content">
        <slot>{{ text }}</slot>
      </div>
      <template v-if="isExpandable" #append>
        <v-btn
          :icon="showDetails ? 'chevron-up' : 'chevron-down'"
          variant="plain"
          density="compact"
          data-testid="so-alert-expand-btn"
          @click="showDetails = !showDetails"
        />
      </template>
      <v-expand-transition v-if="isExpandable">
        <div v-show="showDetails">
          <slot name="expandable"></slot>
        </div>
      </v-expand-transition>
    </v-alert>
  </div>
</template>

<script lang="ts">
  import { ComponentProps } from "@/types/helpers"
  import { defineComponent, PropType } from "vue"
  import { VAlert } from "vuetify/components"

  export type AlertTypes = "success" | "info" | "warning" | "error" | "default"

  type AlertVariants = {
    [key in AlertTypes]: Partial<ComponentProps<typeof VAlert>>
  }

  export type SoAlertProps = {
    type?: AlertTypes
    title?: string
    text?: string
    showCloseButton?: boolean
  }

  export default defineComponent({
    inheritAttrs: false,
    props: {
      type: {
        type: String as PropType<AlertTypes>,
        default: "warning",
      },
      title: {
        type: String,
        default: "",
      },
      text: {
        type: String,
        default: "",
      },
      showCloseButton: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["close"],
    data() {
      return {
        showDetails: false,
      }
    },
    computed: {
      alertAttrs() {
        const variants: AlertVariants = {
          success: {
            color: "success",
            icon: "check-circle",
          },
          info: {
            color: "info",
            icon: "info",
          },
          warning: {
            color: "warning",
            icon: "info",
          },
          error: {
            color: "error",
            icon: "info",
          },
          default: {
            color: "default",
            icon: "info",
          },
        }
        return variants[this.type]
      },
      isExpandable() {
        return !!this.$slots.expandable
      },
    },
  })
</script>

<style scoped>
  :deep(.v-alert--variant-outlined .v-alert__underlay) {
    inset: 0;
    pointer-events: none;
    background-color: currentColor;
    opacity: var(--v-activated-opacity);
  }

  :deep(.so-alert--warning .v-alert__content),
  :deep(.so-alert--default .v-alert__content) {
    color: rgb(var(--v-theme-on-surface)) !important;
  }
</style>
